import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import "./login.css";

function Login({ setAuth }) {
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [apiError, setApiError] = useState(""); // For API error messages
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.message) {
      toast.info(location.state.message, {
        position: "top-center",
        autoClose: 3000,
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    if (apiError) {
      setApiError("");
    }
  };

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(""); // Clear previous API errors
    
    if (!validate()) return;

    setIsLoading(true);

    try {
      const loginData = { 
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      };

      const response = await axios.post(
        "https://parksy-backend.onrender.com/api/auth/login",
        loginData,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 15000,
        }
      );

      if (response.data.success) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));

        if (setAuth) {
          setAuth({
            isAuthenticated: true,
            user: response.data.user,
            token: response.data.token
          });
        }

        toast.success(response.data.message || "Login successful!", {
          position: "top-center",
          autoClose: 2000,
        });

        setTimeout(() => {
          const redirectTo = location.state?.from?.pathname || "/home";
          navigate(redirectTo);
        }, 1500);
      }
    } catch (err) {
      console.error('Login error:', err);
      handleLoginError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginError = (err) => {
    let errorMessage = "Login failed. Please try again.";
    let shouldResendVerification = false;
    
    if (err.response) {
      const { status, data } = err.response;
      
      if (data?.message) {
        errorMessage = data.message;
      }

      // Set API error message to display below form
      setApiError(errorMessage);

      if (status === 403 && (data?.isVerified === false || errorMessage.includes("verify"))) {
        shouldResendVerification = true;
      }

      if (status === 401) {
        // Don't show toast for these, we'll show the error below the form
        return;
      }

      if (status === 400) {
        return;
      }

      if (status === 500) {
        errorMessage = "Server error. Please try again later.";
      }
    } else if (err.code === "ECONNABORTED") {
      errorMessage = "Request timed out. Please check your connection.";
    } else if (err.request) {
      errorMessage = "No server response. Please check your network connection.";
    } else if (err.message.includes("Network Error")) {
      errorMessage = "Network error. Please check your internet connection.";
    }

    setApiError(errorMessage);

    if (shouldResendVerification) {
      const toastId = toast.error(
        <div className="verification-toast-content">
          <p className="error-message">{errorMessage}</p>
          <motion.button 
            onClick={() => {
              toast.dismiss(toastId);
              handleResendVerification();
            }}
            className="resend-verification-button"
            disabled={isResending}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isResending ? "Sending..." : "Resend verification email"}
          </motion.button>
        </div>,
        { 
          position: "top-center",
          autoClose: false,
          closeOnClick: false,
        }
      );
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      const email = formData.email.trim().toLowerCase();
      if (!email) {
        toast.error("Please enter your email first");
        return;
      }

      const response = await axios.post(
        "https://parksy-backend.onrender.com/api/auth/resend-verification",
        { email },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 15000,
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Verification email sent!");
      } else {
        throw new Error(response.data.message || "Failed to resend verification");
      }
    } catch (err) {
      console.error("Resend error:", err);
      const errorMessage = err.response?.data?.message || "Failed to resend verification email";
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="login-container">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="login-card"
      >
        {/* Simple Header */}
        <div className="login-header">
          <div className="brand">
            <span className="brand-icon">🅿️</span>
            <h1 className="brand-name">Parksy</h1>
          </div>
          <p className="welcome-text">Welcome back</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form">
          {/* API Error */}
          {apiError && (
            <div className="error-alert">
              {apiError}
            </div>
          )}

          {/* Email Field */}
          <div className="input-group">
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Email address"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          {/* Password Field */}
          <div className="input-group">
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              className={errors.password ? 'error' : ''}
            />
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="login-button"
          >
            {isLoading ? "Signing in..." : "Sign in"}
          </button>

          {/* Links */}
          <div className="form-links">
            <Link to="/forgot-password" className="link">Forgot password?</Link>
            <Link to="/register" className="link">Create account</Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default Login;